import React, { useEffect, useState } from "react";
import FilePreview from "../shared/FilePreview.jsx";
import { mimeFromName } from "../../utils/zipImport";

// Read at most this much of a text file into memory for the inline preview — a
// stray multi-GB .log shouldn't be slurped into the tab.
const TEXT_PREVIEW_CAP = 256 * 1024; // 256 KB

/**
 * Preview for a not-yet-uploaded importer file. The bytes live in a local `File`
 * handle (from a folder/zip selection), so we mint an `URL.createObjectURL` for
 * the media viewers and revoke it the moment the selection changes or the
 * component unmounts — large audio/video previews must not leak object URLs.
 *
 * `text/*` files have no playable URL, so we read a capped slice locally and
 * hand it to <FilePreview> as `textContent`.
 *
 * @param {{file: File, name: string}} fileNode  staging-tree file node
 */
const ImportFilePreview = ({ fileNode }) => {
    const file = fileNode ? fileNode.file : null;
    const name = fileNode ? fileNode.name : "";
    // Picked/dropped files usually carry a browser-assigned type; zip-extracted
    // ones get one from mimeFromName at extraction time. Fall back to the
    // extension when the type is missing (some assets, e.g. .exr, report none).
    const mimetype = (file && file.type) || mimeFromName(name);

    // This component is remounted per selection (key={selectedKey} in the
    // wizard), so `file` is fixed for an instance. Mint the object URL
    // synchronously at mount — an instant local source shouldn't flash the
    // "Loading…" placeholder — and revoke it on unmount.
    const [url] = useState(() => (file ? URL.createObjectURL(file) : null));
    useEffect(
        () => () => {
            if (url) URL.revokeObjectURL(url);
        },
        [url]
    );

    const [textContent, setTextContent] = useState(null);

    // Local text read (capped), only for text/* files.
    useEffect(() => {
        setTextContent(null);
        const isText = (mimetype || "").split("/")[0] === "text";
        if (!file || !isText) return undefined;

        let cancelled = false;
        const slice = file.size > TEXT_PREVIEW_CAP ? file.slice(0, TEXT_PREVIEW_CAP) : file;
        slice
            .text()
            .then((t) => {
                if (cancelled) return;
                if (file.size > TEXT_PREVIEW_CAP) {
                    // A byte-offset cut can land mid-codepoint, leaving a trailing
                    // U+FFFD; drop it before appending the truncation marker.
                    setTextContent(`${t.replace(/�+$/, "")}\n\n… (preview truncated)`);
                } else {
                    setTextContent(t);
                }
            })
            .catch(() => {
                // Unreadable → resolve to an empty body rather than a stuck
                // "Loading…" state (null is reserved for "still reading").
                if (!cancelled) setTextContent("");
            });
        return () => {
            cancelled = true;
        };
    }, [file, mimetype]);

    if (!fileNode) return null;

    return <FilePreview url={url} name={name} mimetype={mimetype} textContent={textContent} compact />;
};

export default ImportFilePreview;
